import React, { createContext, useContext, useState } from 'react';
import { Accordion, AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger } from '@radix-ui/react-accordion';

import FacetAutoComplete from 'components/common/AutoComplete';
import { CheckboxFacet } from 'components/search/CheckboxFacet/CheckboxFacet';
import { RadioFacet } from 'components/search/RadioFacet/RadioFacet';
import { ButtonFacet } from 'components/search/ButtonFacet/ButtonFacet';
import { SwatchFacet } from 'components/search/SwatchFacet/SwatchFacet';
import { getFacetVariant, getSelectedFacetNames, transformFacetValues } from 'components/search/FacetAccordion/FacetAccordion.utils';
import { FACET_VALUE_TRUNCATION_THRESHOLD } from 'components/search/FacetAccordion/FacetAccordion.constants';
import { BRAND_NAME_FACET, REAL_BRAND_FACET } from 'helpers/SearchUtils';
import { cn } from 'helpers/classnames';
import type { AutoCompleteDataType, FacetDataType, FacetValueType, NavigationVersion } from 'types/search';
import type { FacetAccordionContextValue, FacetValueBaseProps } from 'components/search/FacetAccordion/FacetAccordion.types';
import PlusSmallIcon from 'components/common/IconsV2/PlusSmallIcon';
import MinusSmallIcon from 'components/common/IconsV2/MinusSmallIcon';

const FacetAccordionContext = createContext<FacetAccordionContextValue | null>(null);

const useFacetAccordionContext = () => {
  const context = useContext(FacetAccordionContext);
  if (!context) {
    throw new Error('FacetAccordion compound components must be used within a FacetAccordion.Item');
  }
  return context;
};

const FacetAccordionRoot = Accordion;

type FacetAccordionItemProps = React.ComponentProps<typeof AccordionItem> & {
  facet: FacetDataType;
  context?: string;
};

const FacetAccordionItem = ({ className, facet, context, ...props }: FacetAccordionItemProps) => {
  const contextValue: FacetAccordionContextValue = {
    context,
    facet
  };

  return (
    <FacetAccordionContext.Provider value={contextValue}>
      <AccordionItem
        className={cn('group/item flex flex-col gap-3 border-b border-b-gray-300 last:border-b-0 data-[state=open]:pb-4', className)}
        {...props}
      />
    </FacetAccordionContext.Provider>
  );
};

type FacetAccordionTriggerProps = React.ComponentProps<typeof AccordionTrigger>;

const FacetAccordionTrigger = ({ className, children, ...props }: FacetAccordionTriggerProps) => {
  const { facet } = useFacetAccordionContext();
  const selectedNames = getSelectedFacetNames(facet);

  return (
    <AccordionHeader>
      <AccordionTrigger
        className={cn(
          'relative flex w-full flex-1 appearance-none items-baseline gap-1 py-4 text-left data-[state=open]:pb-0 max-md:group-first/item:pt-0 max-md:group-last/item:pb-0',
          selectedNames && 'data-[state=closed]:pb-2',
          className
        )}
        {...props}
      >
        <div className="relative flex w-full min-w-0 flex-col items-start">{children}</div>

        <div className="shrink-0 text-gray-900">
          <PlusSmallIcon size={16} className="hidden group-data-[state=closed]/item:block" />
          <MinusSmallIcon size={16} className="hidden group-data-[state=open]/item:block" />
        </div>
      </AccordionTrigger>
    </AccordionHeader>
  );
};

type FacetAccordionDisplayNameProps = React.ComponentProps<'span'>;

const FacetAccordionDisplayName = ({ className, ...props }: FacetAccordionDisplayNameProps) => {
  const { facet } = useFacetAccordionContext();
  const { facetFieldDisplayName } = facet;

  return (
    <span className={cn('body-m-medium', className)} {...props}>
      {facetFieldDisplayName}
    </span>
  );
};

type FacetAccordionSelectedValueNamesProps = React.ComponentProps<'span'>;

const FacetAccordionSelectedValueNames = ({ className, ...props }: FacetAccordionSelectedValueNamesProps) => {
  const { facet } = useFacetAccordionContext();
  const selectedNames = getSelectedFacetNames(facet);

  return (
    <span
      className={cn(
        'body-xs w-full truncate text-gray-500 group-data-[state=open]/item:min-h-[1lh] group-data-[state=closed]:empty:hidden',
        className
      )}
      {...props}
    >
      {selectedNames}
    </span>
  );
};

type FacetAccordionContentProps = React.ComponentProps<typeof AccordionContent>;

const FacetAccordionContent = ({ className, ...props }: FacetAccordionContentProps) => (
  <AccordionContent className={cn('flex flex-col gap-2 empty:hidden', className)} {...props} />
);

type FacetAccordionAutoCompleteProps = {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const FacetAccordionAutoComplete = ({ onChange, ...props }: FacetAccordionAutoCompleteProps) => {
  const { facet } = useFacetAccordionContext();
  const { facetField, facetFieldDisplayName, values } = facet;

  const handleAutoComplete = (value: FacetValueType, searchText: string): boolean => {
    /**
     * Replace Umlaute (äöüÄÖÜ) characters with US characters (aouAOU)
     * For brands such as "Fjällräven"
     */
    const displayName = value.displayName
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036F]/g, '');
    return displayName.includes(searchText.toLowerCase());
  };

  const isBrandFacet = facetField === BRAND_NAME_FACET || facetField === REAL_BRAND_FACET;

  if (!isBrandFacet) {
    return null;
  }

  // Always use original values for autocomplete filtering, not filtered values
  return (
    <FacetAutoComplete id={facetField} title={facetFieldDisplayName} values={values} handler={handleAutoComplete} onChange={onChange} {...props} />
  );
};

type FacetAccordionValueListProps = Omit<React.ComponentProps<'ul'>, 'children'> & {
  autoComplete?: AutoCompleteDataType;
  navigationVersion?: NavigationVersion;
  isShowMoreLess?: boolean;
  children: ({ facetValue }: { facetValue: FacetValueType }) => React.ReactNode;
};

const FacetAccordionValueList = ({
  autoComplete,
  navigationVersion,
  isShowMoreLess = false,
  className,
  children,
  ...props
}: FacetAccordionValueListProps) => {
  const { facet } = useFacetAccordionContext();
  const [isExpanded, setIsExpanded] = useState(false);

  const variant = getFacetVariant(facet, navigationVersion);

  const isBrandFacet = facet.facetField === BRAND_NAME_FACET || facet.facetField === REAL_BRAND_FACET;
  const isTxAttrFacet = facet.facetField.startsWith('txAttrFacet_');

  const { groupedValues, sortedGroups } = transformFacetValues({
    facet,
    autoComplete,
    navigationVersion,
    shouldPinSelected: isShowMoreLess && isTxAttrFacet,
    isExpanded
  });

  // Flatten all values to determine total count for truncation
  const totalValueCount = sortedGroups.reduce((sum, group) => sum + (groupedValues[group]?.length ?? 0), 0);
  const isTruncatable = isShowMoreLess && isTxAttrFacet && totalValueCount > FACET_VALUE_TRUNCATION_THRESHOLD;
  const shouldTruncate = isTruncatable && !isExpanded;

  const variantLayout: Record<string, string> = {
    button: 'grid grid-cols-5 md:grid-cols-4 gap-2',
    swatch: 'grid max-md:grid-cols-2 gap-2'
  };

  const ulClassName = variantLayout[variant] || 'grid grid-cols-1';

  return (
    <div>
      <ul className={cn(ulClassName, isBrandFacet && 'scrollbar-hidden max-h-[350px] overflow-y-auto', className)} {...props}>
        {sortedGroups.map(group => {
          const facetValues = groupedValues[group] ?? [];
          const visibleValues = shouldTruncate ? facetValues.slice(0, FACET_VALUE_TRUNCATION_THRESHOLD) : facetValues;

          return (
            <React.Fragment key={group}>
              {group && (
                <li className="pt-1">
                  <span className="body-s text-gray-600">{group}</span>
                </li>
              )}

              {visibleValues.map(facetValue => (
                <React.Fragment key={facetValue.name}>{children({ facetValue })}</React.Fragment>
              ))}
            </React.Fragment>
          );
        })}
      </ul>

      {isTruncatable && (
        <button
          type="button"
          className="body-s mt-4 cursor-pointer border-b border-b-gray-900 pb-[1px] text-gray-900 hover:border-b-2 hover:pb-0"
          onClick={() => setIsExpanded(prev => !prev)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? `Show less ${facet.facetFieldDisplayName} options` : `Show all ${facet.facetFieldDisplayName} options`}
        >
          {isExpanded ? 'Show Less' : 'Show All'}
        </button>
      )}
    </div>
  );
};

type FacetAccordionValueProps = React.ComponentProps<'li'> & {
  facetValue: FacetValueType;
  navigationVersion?: NavigationVersion;
  onFacetSelect: () => void;
};

const FacetAccordionValue = ({ className, facetValue, navigationVersion, onFacetSelect, ...props }: FacetAccordionValueProps) => {
  const { facet, context } = useFacetAccordionContext();
  const { displayName, count, selected, name } = facetValue;

  const variant = getFacetVariant(facet, navigationVersion);

  const baseProps: FacetValueBaseProps = {
    label: displayName,
    count: (count ?? 0).toString(),
    isSelected: selected ?? false,
    onClick: () => onFacetSelect()
  };

  switch (variant) {
    case 'radio':
      return (
        <li className={cn('py-2 first:pt-0 last:pb-0', className)} {...props}>
          <RadioFacet type="radio" {...baseProps} name={context ? `${context}-${facet.facetField}` : facet.facetField} value={name} />
        </li>
      );
    case 'button':
      return (
        <li className={cn('last:pb-0', className)} {...props}>
          <ButtonFacet type="button" {...baseProps} />
        </li>
      );
    case 'checkbox':
      return (
        <li className={cn('py-2 first:pt-0 last:pb-0', className)} {...props}>
          <CheckboxFacet type="checkbox" {...baseProps} />
        </li>
      );
    case 'swatch':
      return (
        <li className={className} {...props}>
          <SwatchFacet type="swatch" {...baseProps} imgUrl={facetValue?.metadata?.imgUrl} />
        </li>
      );
  }
};

/**
 * Renders a visually-hidden list of crawlable `<a href>` links for all
 * non-size facet values. Place inside FacetAccordion.Item but outside
 * FacetAccordion.Content so the links are always in the DOM regardless
 * of accordion open/closed state.
 *
 * Why sr-only + aria-hidden + tabIndex={-1}?
 * This may look like an anti-pattern (hidden text = keyword stuffing),
 * but it is not. The accordion content is lazily rendered, so crawlers
 * cannot discover filter links behind the closed accordion. These
 * hidden anchors provide real, navigable URLs that mirror the
 * interactive filters — they are structural navigation aids, not
 * keyword text. aria-hidden and tabIndex={-1} prevent duplicate
 * exposure to assistive tech, since the fully accessible version
 * already exists inside the accordion content.
 *
 * Note: the HTML `hidden` attribute (display: none) would also be
 * crawled by Google, but hidden content may receive less ranking
 * weight. sr-only is the more conservative choice — it keeps the
 * links rendered (just off-screen), avoiding any ambiguity about
 * how crawlers treat display: none content.
 *
 * Size facets (symbolicSizingField) are excluded — they produce
 * non-indexable pages and should not be discoverable by crawlers.
 */
const FacetAccordionSeoLinks = () => {
  const { facet } = useFacetAccordionContext();

  if (facet.symbolicSizingField) {
    return null;
  }

  const links = facet.values
    .map(v => ({ name: v.displayName, href: v.facetZsoUrl || v.facetUrl }))
    .filter((v): v is { name: string; href: string } => !!v.href);

  if (links.length === 0) {
    return null;
  }

  return (
    <nav aria-hidden="true" className="sr-only">
      {links.map(({ name, href }) => (
        <a key={href} href={href} tabIndex={-1} rel="nofollow">
          {name}
        </a>
      ))}
    </nav>
  );
};

export const FacetAccordion = {
  Root: FacetAccordionRoot,
  Item: FacetAccordionItem,
  Trigger: FacetAccordionTrigger,
  DisplayName: FacetAccordionDisplayName,
  SelectedValueNames: FacetAccordionSelectedValueNames,
  Content: FacetAccordionContent,
  AutoComplete: FacetAccordionAutoComplete,
  ValueList: FacetAccordionValueList,
  Value: FacetAccordionValue,
  SeoLinks: FacetAccordionSeoLinks
};
